/**
 * Addressing a scene object from serialized input: a tool call, a command
 * line, anything that names an object by string. In-process APIs take
 * `SceneObject` references; this is the one place a string becomes one.
 *
 * The rule:
 * 1. An exact path, as `getPath()` writes it (`Scene/Room/Chair`). A leading
 *    `/` makes the path absolute, with or without the engine root's name.
 *    A path is usually unique: create-from-data, an editing-session load, a
 *    dissolve, `reparentSceneObject` and the `transform.setParent`
 *    interceptor give a newcomer a name of its own among its siblings. Paths
 *    that still collide (a scene loaded while playing, content inside a scene
 *    reference, a raw `SceneObject.setParent`) are `AMBIGUOUS`.
 * 2. Otherwise the objects whose path ends with `ref` at a segment boundary.
 *    A bare name is the one-segment case.
 * 3. One match resolves. None is `NOT_FOUND`, several are `AMBIGUOUS` with the
 *    candidate paths, and a match whose transform is gone is `DISPOSED`.
 *
 * The engine root is never a match. Keywords such as "selected" are the
 * caller's to handle before it gets here.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';

/** How many candidate paths a refusal lists. */
const MAX_CANDIDATES = 8;

export type ObjectRefResult =
    | { object: SceneObject }
    | { error: 'NOT_FOUND' | 'AMBIGUOUS' | 'DISPOSED'; message: string; candidates: string[] };

/** Resolve `ref` to exactly one scene object, or say why it can't be. */
export function resolveObjectRef(scene: TraceScene, ref: string): ObjectRefResult {
    const engineRoot = scene.getSceneRoot();
    const objects = scene.getSceneObjects().filter(object => object !== engineRoot);

    let matches: SceneObject[];
    if (ref.startsWith('/')) {
        const absolute = ref.slice(1);
        const rooted = engineRoot ? `${engineRoot.name}/${absolute}` : absolute;
        matches = objects.filter(object => {
            const path = object.getPath();
            return path === absolute || path === rooted;
        });
    } else {
        matches = objects.filter(object => object.getPath() === ref);
        if (matches.length === 0 && ref !== '') {
            const suffix = `/${ref}`;
            matches = objects.filter(object => object.getPath().endsWith(suffix));
        }
    }

    const live = matches.filter(object => !object.transform.isDisposed());
    if (live.length === 1) return { object: live[0] };
    if (live.length > 1) {
        const candidates = live.slice(0, MAX_CANDIDATES).map(object => object.getPath());
        const more = live.length > MAX_CANDIDATES ? `, and ${live.length - MAX_CANDIDATES} more` : '';
        return {
            error: 'AMBIGUOUS',
            message: `"${ref}" is ambiguous — ${live.length} matches. Pass one of these full paths instead: `
                + `${candidates.map(path => `"${path}"`).join(', ')}${more}.`,
            candidates,
        };
    }
    if (matches.length > 0) {
        return { error: 'DISPOSED', message: `"${ref}" names an object that has been disposed.`, candidates: [] };
    }
    return { error: 'NOT_FOUND', message: `Scene object "${ref}" not found.`, candidates: [] };
}
